/**
 * Email sending health — the two rates that can cost a tenant their own AWS
 * account, and what happened to everything that did not arrive.
 *
 * **This is the top of the dashboard and the order is a decision, not a
 * preference.** The worst thing that can happen in this product is a tenant
 * getting their AWS account put under review or suspended. Under
 * bring-your-own-SES that lands on the customer, not on Wend, and they find out
 * from Amazon rather than from us unless this screen tells them first. So bounce
 * and complaint lead, deliverability follows because slices 5 and 6 keep
 * *accepted* and *delivered* apart on purpose, and everything else on the page
 * is below them.
 *
 * ## THE DENOMINATOR IS EMAIL, AND SAYING SO IS NOT PEDANTRY
 *
 * Every numerator here is an SES event and every threshold is Amazon's, so
 * WhatsApp is not in the denominator — not here, not anywhere on this card.
 * Slice 10 shipped the opposite and it is the worst defect this codebase has
 * recorded: a workspace sitting exactly on AWS's 5% line rendered as **1.55%
 * behind a green badge**, because two thousand WhatsApp sends were counted in.
 * The API computes it that way now; what this file must not do is put the two
 * channels in one visual frame and let a reader supply the confusion themselves.
 *
 * ## SLICE 20 SPLIT IT IN TWO, AND THE SPLIT IS THE LAYOUT
 *
 * {@link SendingHealthTiles} is above the fold: three tiles and one line of
 * window. {@link NotDeliveredCard} is below it, because the five reasons a
 * message did not arrive are what a reader goes looking for *after* a rate has
 * told them to look — nobody opens a dashboard to read a rendering-failure
 * count. That ordering is the same "does the number change a decision" test the
 * whole screen is built on, applied to two halves of one subject.
 *
 * What went with the split is the prose. The old card carried a six-line
 * description arguing the WhatsApp point, a five-line panel description arguing
 * the five-reasons point, and a paragraph under each tile. All of it is in this
 * header now. What survives on screen is the honesty: the window, the counts
 * beside every rate, and one line per reason saying what it is and who fixes it.
 *
 * ## THE FIVE REASONS STAY FIVE
 *
 * Collapsing them into one "failed" number hides the only ones anybody can act
 * on. A hard bounce is a dead address and the list is already repaired. A soft
 * bounce is a full or throttled mailbox, and cleaning it off the list throws
 * away a good contact. A delay has not failed at all. A rendering failure is our
 * own template breaking on a variable — the only one of the five that is Wend's
 * fault and the only one a customer can fix today. They are not summed, and the
 * undetermined bounces are reported separately rather than filed as soft,
 * because calling them retryable would be a guess.
 *
 * ## ACCEPTED, AND NOTHING EVER CAME BACK — A STATE THE PARKED BUILD DREW GREEN
 *
 * The three arms {@link RateKpi} had then — nothing sent, under the floor, over
 * it — were all about *how many messages there are*. None of them asks whether
 * any of those messages has an outcome, and on the live deployment none has:
 * SNS is not wired, so over a thousand accepted emails sat in `awaitingEvents`
 * with every outcome count at zero. That cleared the volume floor, the API
 * banded `0 / 1,116` as `ok`, and the tile printed **"0.00% — Under the line"**
 * in green. The card below it listed five reasons a message did not arrive,
 * each with a confident `0`.
 *
 * So both halves of this file ask `noOutcomeReported` first, before any rate or
 * reason is drawn, and say `NO_OUTCOME_REPORTED_COPY` instead: the accepted
 * count, which is a fact from this database, and the sentence saying nobody has
 * reported on it. The alternative — leaving the tiles and adding a caveat line —
 * was rejected, because a green verdict with a sentence under it is still a
 * green verdict, and the sentence is the part a skimmer does not read.
 *
 * The partial case (some outcomes back, most still waiting) used to render the
 * API's rate over everything accepted, and so read clean: twelve reports back
 * from 1,116 accepted was a green `0 / 1,116`. Slice 19 took the denominator
 * decision where it belonged, in `overview-sending.ts`: the rates are over
 * settled outcomes, twelve is under the floor, and each tile carries the
 * unsettled remainder beside its figure (`AwaitingRemainder`).
 *
 * **A complaint is a report, and it is said as one — without lifting the gate.**
 * Slice 19 first let a complaint end the nothing-reported state, and this card
 * then printed Delivered 0, Bounced 0 and five reasons at 0 as facts over a window
 * in which Amazon had reported not one delivery or bounce. The gate is about
 * deliveries and bounces again; a complaint count is printed inside it, and the
 * sentence is the one that does not say no complaint came back
 * (`unreportedExplanation`, which takes the count).
 *
 * **And bounces with not one delivery get no rate** (`onlyBouncesReported`): the
 * API withholds it, the tiles say why, and this card leaves out a Delivered
 * figure of 0 it cannot stand behind.
 *
 * **Slice 19 made the sentence certain where it can be.** Over the same state,
 * `deliveryReports` says whether a report has *ever* reached Wend for this
 * workspace, and `unreportedExplanation` turns the pair into one of three
 * answers: never — said flatly, as a setup fact, with the first missing step and
 * a link to where it is fixed; received before — so the cause is not setup
 * alone, and the sentence names what else it can be; or not
 * known, on an older API, where slice 20's hedged sentence is still the most
 * that can honestly be said. Both halves of this file ask it the same way.
 *
 * ## SLICE 27: A PROJECTION UNDER THAT STATE, AND WHAT IT IS NOT ALLOWED TO BE
 *
 * The state above is the one this deployment is permanently in, and for a reader
 * it is all absence: a count, a chip, and a link to a form. The owner asked three
 * times for assumed delivery, open and click figures until real ones arrive, and
 * {@link ProjectedFigureRow} is the answer.
 *
 * It does not touch a line of the reasoning above it. Nothing here is softened,
 * no rate is invented, no band is drawn and no zero is printed as a fact. The
 * projection is a separate element, under the strip that says nothing has been
 * measured, carrying its own chip and its own assumptions in full sight. The
 * measured figures beside it are the same measured figures.
 *
 * Three properties do the work, and all three live in `@wend/shared`'s
 * `projection.ts` rather than here, because a rule enforced by a screen is a rule
 * one screen enforces:
 *
 *   - it is projected over `awaitingEvents` — the unreported remainder — so it is
 *     strictly additive to what is measured and shrinks by itself as reports land;
 *   - it is `null` from the first provider notification of any kind, with no flag
 *     to leave on and no deploy needed to end it;
 *   - {@link ProjectedFigureRow} has no prop a measured count could be passed
 *     through, so the two cannot be confused by a later edit here.
 *
 * What is still true, and is written in the contract's header as well as this
 * one: it is a number nobody measured, and *"everything arrived"* is the
 * assumption that could mislead — not the 30% or the 20%.
 *
 * ## NOTHING SENT, AND THE ROW OF ZEROS UNDER IT
 *
 * The split put the tiles and this card on two different conditions, and only
 * the tiles kept the old one. Master hid the counts and the five reasons
 * entirely when nothing had been accepted; the parked split asked only
 * `noOutcomeReported`, which is false at `accepted === 0` by design, so a
 * workspace that had sent nothing got *"No email sent in the last 30 days … not
 * zero ones"* above the fold and, one card down, **Delivered 0, Bounced 0,
 * Complaints 0 and five reasons at 0**. `accepted === 0` is now asked first
 * here as it is in {@link SendingHealthTiles}, and it prints one sentence and no
 * figure. The card itself stays, because its heading and window are still true.
 */
import type { ReactNode } from 'react';

import {
  OVERVIEW_RATE_THRESHOLDS,
  noOutcomeReported,
  onlyBouncesReported,
  type BounceBreakdown,
  type DeliveryReports,
  type NotDeliveredBreakdown,
  type OverviewResponse,
  type OverviewWindow,
  type ProjectedFigures,
} from '@wend/shared';
import { Card, CardContent, CardHeader, CardTitle, StateChip } from '@wend/ui';
import { Link } from 'react-router-dom';

import { unreportedExplanation } from '../../lib/delivery-reports-display';
import { formatCount, formatDate, pluralise } from '../../lib/format';
import { integrationTabPath } from '../integrations/integration-tab-url';
/* Across a route boundary, as `ActivityCharts` and `ListHealthPanel` already do.
   `MoreDetail`'s own header says it belongs in `components/` and says why the
   move was deferred; it is still deferred, and still written down — moving a
   file eleven call sites deep is not this slice's risk to take while the gate
   items are open. */
import { MoreDetail } from '../templates/MoreDetail';
import { CountTile, DeliverabilityKpi, Figure, RateKpi } from './OverviewFigures';
import { ProjectedFigureRow } from '../../components/ProjectedFigures';
import { formatRate } from './overview-format';

/* -------------------------------------------------------------------------- */
/* Above the fold                                                              */
/* -------------------------------------------------------------------------- */

/**
 * The cohort the rates are drawn over, and what Amazon reported outside it.
 *
 * ## THE DISAGREEMENT THIS CLOSES
 *
 * Wend learns about an email two ways. Its own delivery reports configuration
 * set emits `Send`, then `Delivery` or `Bounce`. The sending identity's own SNS
 * topic — set up in the AWS console, and the usual arrangement before slice 19 —
 * emits bounces and complaints and **never a delivery**.
 *
 * Slice 24 item 16 narrowed every rate to the first of those, because counting
 * the second gave the denominator no deliveries and made every rate read high:
 * a window of 2,000 set-reported messages beside 1,000 identity bounces read 36%
 * where it should read 4%. That narrowing is right and is not touched here.
 *
 * What it left behind is a disagreement. `settled` went onto the wire narrowed,
 * `delivered` and `bounced` stayed unnarrowed beside it, and this screen went on
 * re-deriving `onlyBouncesReported` from the unnarrowed pair — so the API and
 * the screen asked the same question of two different populations. On a
 * workspace whose only reports are identity bounces the API answered "not the
 * lopsided shape" and the screen answered "it is", the denominator branch was
 * reached first, and the tile printed **"Amazon has reported no delivery and no
 * bounce for any of them"** over fifty bounces Amazon had reported.
 *
 * **A screen denying a report it holds is the same defect as a screen inventing
 * a figure it does not, pointed the other way.** Both are the page's own rule —
 * zero and "not measured" are different statements — and only one of them had
 * ever been looked for.
 *
 * The repair needs no contract change, because both narrowed numerators are
 * already on the wire: `deliverability.numerator` is the cohort's deliveries and
 * `bounceRate.reading.numerator` its bounces, each computed by the API from the
 * same cohort it decided `withheld` from. This asks those, and returns the
 * remainder so the screen can say what it knows rather than deny it.
 */
function cohortReading(sending: OverviewResponse['sending']): {
  readonly onlyBounces: boolean;
  readonly bouncedOutsideCohort: number;
} {
  const cohortDelivered = sending.deliverability.numerator;
  const cohortBounced = sending.bounceRate.reading.numerator;

  return {
    onlyBounces: onlyBouncesReported({
      delivered: cohortDelivered,
      bounced: cohortBounced,
      awaiting: sending.awaitingEvents,
    }),
    // Never negative: a wire where the cohort exceeds the whole is a wire this
    // screen cannot repair, and a negative count would be a second lie.
    bouncedOutsideCohort: Math.max(0, sending.bounced - cohortBounced),
  };
}

export function SendingHealthTiles({
  sending,
  window,
  deliveryReports,
  projection,
  engagementSlot,
}: {
  sending: OverviewResponse['sending'];
  window: OverviewWindow;
  /** Slice 19. Null when the response did not carry it — an older API. */
  deliveryReports: DeliveryReports | null;
  /**
   * Slice 27. Non-null only where no provider report of any kind has ever
   * reached Wend, which is also the only branch below that renders it: a
   * workspace with real outcomes gets the measured tiles and nothing assumed.
   */
  projection: ProjectedFigures | null;
  /**
   * The Opened / Clicked row, rendered only in the measured arm.
   *
   * A `ReactNode`, never an `AsyncData<EngagementResponse>`. This file's whole
   * argument is that it holds ONE channel and ONE contract — `GET /overview`'s
   * email-only sending block — and putting a second request's three async
   * states into it would put a slow provider call in the same component as the
   * two rates that can cost somebody their AWS account.
   *
   * The slot also gets mutual exclusion with `ProjectedFigureRow` for free:
   * that row is drawn only by `NoOutcomeReported`, which is a different arm, so
   * an assumed Opened can never appear beside a measured one.
   */
  engagementSlot?: ReactNode;
}) {
  /*
   * Slice 33 gave this component a card of its own in `HomePage`. This branch
   * used to supply its own border because nothing else did; keeping it would
   * now draw a line-coloured border a few pixels inside an identical one. The
   * warn-tinted callout in `SetUpDeliveryReports` keeps its border — that one
   * is a different colour and reads as an object rather than a seam.
   */
  if (sending.accepted === 0) {
    return (
      <div className="flex flex-col gap-2">
        <p className="text-[1.05rem] font-semibold tracking-[-0.015em] text-text">
          No email sent in the last {window.days} days
        </p>
        <p className="max-w-prose text-[0.86rem] leading-relaxed text-muted">
          So there is no bounce rate, no complaint rate and no deliverability figure — not zero
          ones.
        </p>
      </div>
    );
  }

  if (noOutcomeReported({ accepted: sending.accepted, awaiting: sending.awaitingEvents })) {
    return (
      <NoOutcomeReported
        sending={sending}
        window={window}
        deliveryReports={deliveryReports}
        projection={projection}
      />
    );
  }

  const { onlyBounces, bouncedOutsideCohort } = cohortReading(sending);

  /*
   * THE WINDOW LINE IS GONE, AND ITS FACT IS IN THE CARD'S TAG.
   *
   * This used to print "Email only, over the last 30 days — 20 Aug to 19 Sep"
   * directly above the tiles. The owner removed it because it pushed this
   * column out of line with the windowed panel beside it; the channel, the
   * window and both dates are now the card's right-hand tag in `HomePage`,
   * where they cost no vertical rhythm.
   *
   * **It is not simply deleted, and the difference matters.** The sentence was
   * itself a repair: slice 20's split dropped the old card's header and this
   * line with it, and review found it missing. What it defends against is a
   * reader supplying "all my messages" for a figure that is email's alone —
   * the denominator confusion behind one of this project's two criticals. That
   * defence now lives in the tag, asserted there, and the old comment's
   * premise ("they sit directly above a chart of both channels") has been
   * stale since slice 36 took both chart cards off the page.
   */
  return (
    <div className="flex flex-col gap-3">
      {/*
        THE DESIGN'S TWO COUNT TILES, AND THEY LEAD.
        -------------------------------------------------------------------
        The approved design opens this card with `Accepted` and `Awaiting a
        result` — a label and a big number each, no prose, no bar. This build
        went straight to the three rate tiles, which are the same card's
        *second* thought: a rate is a judgement, and the design puts the two
        plain facts in front of it.

        They are also the only two figures here that are true in every state.
        `accepted` and `awaiting` are what this database knows without any
        provider having reported — the same pair
        `OUTCOME_MEASURES_KNOWN_WITHOUT_A_REPORT` names — so these two tiles
        never have to become a dash, and the card always opens with a number
        rather than with an explanation of a missing one.

        The rate tiles keep everything they had; they simply stop being the
        first thing a reader meets.
      */}
      <div data-role="sending-counts" className="flex flex-wrap gap-3">
        {/*
          EACH SENTENCE SPLIT AT THE LINE `TileDetail` DRAWS: what a reader
          could misread the figure without stays on screen, what explains how
          the figure was built folds.

          `It is not a delivery` is the visible half and is not negotiable —
          a reader who takes Accepted for Delivered has made the exact mistake
          behind one of this project's two criticals, and a correction behind a
          chevron is a correction nobody reads.

          `Nothing on this card is measured against it` is the folded half. It
          is true, it is the reason `accepted` is not the denominator of
          anything here, and a reader who never opens it still reads the number
          correctly.
        */}
        <CountTile
          label="Accepted"
          value={sending.accepted}
          meaning="What Amazon took from Wend. It is not a delivery."
          detail={{
            summary: 'What it is measured against',
            /*
             * NOT "every rate beside it is over delivered and bounced", which
             * is what this said first and was wrong by one tile. Deliverability
             * divides by settled outcomes — delivered plus bounced — while the
             * open and click rates in `engagementSlot` divide by delivered
             * alone (`engagement.ts`: "the denominator is delivered rather than
             * accepted"). Naming one denominator for both would have been a
             * false sentence about a cohort, folded where nobody would check
             * it. What is true of all of them is the part that matters here:
             * none of them divides by `accepted`.
             */
            body: 'Nothing on this card. Every rate here divides by messages a provider actually reported on — delivered, or delivered and bounced — never by what Amazon merely accepted.',
          }}
        />
        {/*
          The visible half defines the figure. The folded half is guidance about
          where it is heading — and it is folded rather than deleted because the
          old one-liner ("should trend towards zero") was true of a workspace
          with delivery reports configured and quietly false of one without. The
          fold is what gives the qualification room to be said at all.
        */}
        <CountTile
          label="Awaiting a result"
          value={sending.awaitingEvents}
          meaning="Accepted, with no delivery or bounce reported yet."
          detail={{
            summary: 'When this falls',
            body: 'It falls as Amazon reports outcomes, and should trend towards zero — except for email sent before delivery reports were set up, which has nothing left to report and stays here.',
          }}
        />
      </div>

      {engagementSlot}

      {/*
        DELIVERABILITY STANDS ALONE NOW, AND THE OTHER TWO DID NOT GO FAR.
        ---------------------------------------------------------------------
        The owner removed the Bounce rate and Complaint rate tiles because the
        windowed panel beside this column already carries them. Every fact they
        held has a new home rather than none:

          - Amazon's two thresholds -> the panel's footer, beside the rates
            they judge.
          - "Under the line" / "Climbing towards the line" / "Over the line" ->
            a `Badge` under each panel bar, so the verdict is still a WORD and
            never only a colour.
          - `ThresholdBar`, with Amazon's line drawn as a visible mark -> the
            panel's two rate rows. The same component, relocated; a recoloured
            fill would have kept the length and lost the mark, and the mark is
            what makes amber legible as "near the line" rather than "quite
            full".
          - The 1,000-message floor -> the panel, applied through the same
            `rateReading` the API uses.

        `RateKpi` itself is NOT deleted. It stays exported with no call site,
        as `AbsentRateTile` has since slice 26, because it is the component that
        makes a bounce rate inexpressible without the threshold it is judged by.

        Deliverability stays because the owner's reason for the other two does
        not reach it: the panel draws `Delivered` as a raw count and has no
        delivered-over-settled ratio anywhere. It also carries five refusal
        states — nothing settled, only bounces reported, under the floor, the
        awaiting remainder, and the out-of-cohort bounce — which would be
        orphaned with it.
      */}
      <div data-role="sending-rates" className="flex flex-wrap gap-3">
        <DeliverabilityKpi
          reading={sending.deliverability}
          accepted={sending.accepted}
          awaiting={sending.awaitingEvents}
          onlyBounces={onlyBounces}
          bouncedOutsideCohort={bouncedOutsideCohort}
        />
      </div>
    </div>
  );
}

/**
 * Accepted, and not one outcome reported — see "ACCEPTED, AND NOTHING EVER CAME
 * BACK" in the header.
 *
 * The accepted count is printed because it is true and it is the reader's only
 * way to recognise their own sending in this sentence. No rate, no verdict, no
 * threshold bar and no zero: `warn`-weight border rather than `bad`, because
 * nothing is known to be wrong — what is known is that nothing is known.
 *
 * Spam complaints, where Amazon reported any, are printed too: they are the one
 * outcome in this state that is a fact, and the tile they would have gone in is
 * not drawn.
 */
function NoOutcomeReported({
  sending,
  window,
  deliveryReports,
  projection,
}: {
  sending: OverviewResponse['sending'];
  window: OverviewWindow;
  deliveryReports: DeliveryReports | null;
  /** Slice 27. Null unless a report of any kind has never arrived. */
  projection: ProjectedFigures | null;
}) {
  const explanation = unreportedExplanation('email', deliveryReports, sending.complaints);

  /*
   * WHAT IS MEASURED, AT TILE SIZE — AND NO DASHES — SLICE 26.
   *
   * Slice 25 drew this row as `Accepted` plus three tiles carrying an em-dash
   * and a "No reports yet" chip where the bounce, complaint and deliverability
   * rates would be. The reasoning was that a reader should see *three rates,
   * none of them known*, in the shape they will be in once reports arrive.
   *
   * The owner looked at it and read the dashes as the product failing to show
   * data. That is the more likely reading and it is the one that matters: three
   * em-dashes in the most valuable row on the screen say "broken" before they
   * say "not yet configured", and a reader who concludes the dashboard is
   * broken stops reading the dashboard.
   *
   * So the row carries only figures that are real — `Accepted` and `Awaiting`
   * are both counts from this database, not reports Amazon never sent — and the
   * three unknown rates are replaced by ONE strip that says what is actually
   * true: delivery reports are not set up, here is the first missing step, here
   * is the screen that fixes it.
   *
   * **Nothing is softened.** There is still no rate, no percentage, no verdict
   * and no zero anywhere in this state; `not_set_up` is a stronger and more
   * accurate word than `not_measured`, because Wend knows exactly why it cannot
   * measure and can name the repair. The three tiles come back, with figures in
   * them, the moment a report arrives — this branch is not reached then.
   *
   * `AbsentRateTile` stays exported from `OverviewFigures` and stays
   * value-less — it is still the right carrier for a rate that is genuinely
   * unknowable rather than merely unconfigured, and deleting it would delete the
   * component that makes `0.00% — Under the line` inexpressible. **This file no
   * longer imports it**, because slice 26 stopped drawing it here and an import
   * nothing uses is debris `noUnusedLocals` is not switched on to catch.
   */
  return (
    <div data-state="no-outcome-reported" data-reports={explanation.state} className="flex flex-col gap-2">
      <div className="flex flex-wrap gap-3">
        <CountTile
          label="Accepted"
          value={sending.accepted}
          meaning={`Amazon took these in the last ${String(window.days)} days. Accepted is not delivered.`}
        />
        <CountTile
          label="Awaiting a result"
          value={sending.awaitingEvents}
          meaning="Accepted, with no delivery or bounce reported back for them yet."
        />
        {/* The one outcome Amazon does report in this state: its identity topic
            emits complaints without ever emitting a delivery. A real count, so
            it takes a real tile rather than sitting in a line underneath. */}
        {sending.complaints > 0 && (
          <CountTile
            label="Marked as spam"
            value={sending.complaints}
            meaning="Reported by Amazon, while no delivery or bounce was. A real count, not a rate."
          />
        )}
      </div>

      <SetUpDeliveryReports explanation={explanation} />

      {/*
        AFTER the strip, never before it, and never inside the tile row above.

        The order is the argument: real counts, then the fact that nothing has
        been measured and how to fix it, then — clearly marked — what it would
        look like under a stated assumption. Reversing the last two would let the
        projection read as the answer and the strip as a footnote on it.

        `projection` is `null` for every workspace that has ever had a report of
        any kind, so this is absent the moment Amazon says anything at all. See
        `projection.ts` in `@wend/shared` for why that gate needs no flag.
      */}
      {projection !== null && <ProjectedFigureRow projection={projection} />}

      <UnreportedDetail explanation={explanation} />
    </div>
  );
}

/**
 * The three rates this workspace cannot measure, said once, with the repair.
 *
 * It replaces three em-dashed tiles. A dash in a figure slot is the right mark
 * for a number nobody can know; it is the wrong mark for a number Wend knows
 * exactly how to start collecting, because it reports an absence where the
 * truth is an unfinished setup. `not_set_up` is the state vocabulary's word for
 * that, and it is the one carrying a repair.
 *
 * The three rate names are still on screen, because *which* figures are missing
 * is part of what is being said — a reader who is told only "delivery reports
 * are not set up" does not know that the bounce rate is one of the things they
 * are not getting.
 *
 * The link is shown on exactly the condition `unreportedExplanation` sets: only
 * where the first missing step is a form on Integrations. For a deployment
 * setting, or a workspace whose reports merely stopped, a link to a form that
 * cannot fix it would be worse than no link.
 */
function SetUpDeliveryReports({
  explanation,
}: {
  explanation: ReturnType<typeof unreportedExplanation>;
}) {
  return (
    <div
      data-role="delivery-reports-setup"
      className="flex flex-wrap items-center gap-x-4 gap-y-2 rounded-base border border-warn/40 bg-warn/[0.04] px-4 py-3"
    >
      <StateChip state="not_set_up" label="Delivery reports not set up" />
      <p className="min-w-0 flex-1 text-[0.82rem] leading-snug text-muted">
        No bounce rate, complaint rate or deliverability figure until Amazon reports back.
      </p>
      {explanation.pointsAtSetup && (
        <Link
          to={integrationTabPath('email')}
          className="shrink-0 text-[0.82rem] text-text underline underline-offset-2"
        >
          Set up delivery reports
        </Link>
      )}
    </div>
  );
}

/**
 * The cause, once, one level down.
 *
 * Everything above says *that* nothing is measured, in chips. This says why, and
 * it keeps every word it had — including the clause no chip can carry: that
 * email bounces reach the suppression list only through these reports, so a list
 * is not being cleaned while they are missing.
 *
 * It is `MoreDetail` rather than a paragraph for the reason that component's own
 * header gives: the children are in the DOM while it is closed, so a reader
 * searching the page for "suppression" still finds this, and a screen reader
 * walking the document still meets it.
 */
function UnreportedDetail({ explanation }: { explanation: ReturnType<typeof unreportedExplanation> }) {
  return (
    <MoreDetail summary={explanation.title}>
      <UnreportedParagraphs explanation={explanation} size="text-[0.82rem]" />
      <p>
        Until they arrive, a hard bounce never reaches this workspace’s suppression list through
        Amazon — so the list is not being cleaned by the sends that would clean it.
      </p>
    </MoreDetail>
  );
}

/**
 * The explanation's paragraphs, and — only where the repair is a form on
 * Integrations — the link to it. Shared by the tiles and the card below, so the
 * two cannot word one silence differently.
 */
function UnreportedParagraphs({
  explanation,
  size,
}: {
  explanation: ReturnType<typeof unreportedExplanation>;
  size: string;
}) {
  return (
    <>
      {explanation.paragraphs.map((paragraph) => (
        <p key={paragraph} className={`max-w-prose ${size} leading-relaxed text-muted`}>
          {paragraph}
        </p>
      ))}
      {explanation.pointsAtSetup && (
        <p className={`${size} leading-relaxed`}>
          <Link
            to={integrationTabPath('email')}
            className="text-text underline underline-offset-2 hover:text-text"
          >
            Set up delivery reports on Integrations
          </Link>
        </p>
      )}
    </>
  );
}

/* -------------------------------------------------------------------------- */
/* Below it                                                                    */
/* -------------------------------------------------------------------------- */

interface NotDeliveredRow {
  readonly key: keyof NotDeliveredBreakdown;
  readonly label: string;
  readonly meaning: string;
}

const NOT_DELIVERED_ROWS: readonly NotDeliveredRow[] = [
  {
    key: 'rejected',
    label: 'Rejected',
    meaning: 'Amazon refused it before sending. Usually a suppressed or malformed address.',
  },
  {
    key: 'hardBounced',
    label: 'Hard bounced',
    meaning: 'The address does not exist. Already suppressed; nothing to do.',
  },
  {
    key: 'softBounced',
    label: 'Soft bounced',
    meaning: 'Mailbox full, or throttled. Retryable — do not clean these off the list.',
  },
  {
    key: 'delayed',
    label: 'Delayed',
    meaning: 'Amazon is still trying. Not a failure yet.',
  },
  {
    key: 'renderingFailed',
    label: 'Rendering failed',
    meaning: 'A template variable broke on this contact. This one is ours to fix.',
  },
];

export function NotDeliveredCard({
  sending,
  window,
  deliveryReports,
}: {
  sending: OverviewResponse['sending'];
  window: OverviewWindow;
  /** Slice 19. Null when the response did not carry it — an older API. */
  deliveryReports: DeliveryReports | null;
}) {
  const notDelivered: NotDeliveredBreakdown = sending.notDelivered;
  const bounces: BounceBreakdown = sending.bounces;
  /* Asked before a single outcome is printed. With nothing reported, Delivered,
     Bounced, Complaints and all five reasons would each read `0` — a count of
     reports that never arrived, drawn as a count of things that did not
     happen. See the header. */
  const unreported = noOutcomeReported({
    accepted: sending.accepted,
    awaiting: sending.awaitingEvents,
  });
  /* Bounces and not one delivery: a Delivered figure of 0 is then the one count
     on this card nobody reported, so it is left out and the reason said.

     Through `cohortReading` for the reason its header gives at length: the API
     decides the same predicate over the narrowed send-event cohort, and a card
     that asks it of the unnarrowed counts beside it answers a different question
     from the one the rates above were withheld by. This card reached the same
     defect by the same route as the tiles, and the probe that found it there
     found it here. */
  const { onlyBounces } = cohortReading(sending);
  const explanation = unreportedExplanation('email', deliveryReports, sending.complaints);

  /*
   * A CARD WITH NOTHING TO COUNT DOES NOT RENDER AT ALL — SLICE 26.
   *
   * This card exists to break a failure down by reason. With nothing accepted
   * there is nothing to break down; with nothing reported there are no reasons,
   * only the fact that none arrived — and that fact is already on the tile row
   * directly above, in a chip, with its cause in that row's disclosure.
   *
   * So it used to print, on the deployment that actually sends: `Accepted
   * 1,116` and `Awaiting events 1,116`, both already in the tiles above, under
   * the identical delivery-report sentence those tiles had just folded away.
   * 125 words, one duplicated figure pair, one duplicated explanation, and not
   * one reason — which is the thing the card is for. The owner asked why it was
   * on the Overview at all; in that state the honest answer is that it was not
   * earning its place.
   *
   * **The gate is narrow on purpose, and the narrow part is the interesting
   * part.** `unreported` alone would have been wrong: a DELAY is a report, and
   * so is a COMPLAINT. A workspace whose notifications are arriving perfectly,
   * whose mail is merely slow, has `noOutcomeReported` true and a real
   * `Delayed 60` to show — and hiding that would delete the one figure telling
   * the reader their reports ARE getting through. Amazon's identity topic emits
   * complaints without ever emitting a delivery, which is the same shape again.
   * Both keep the card. Only the state where every figure on it is already on
   * the tiles above is dropped.
   *
   * **Nothing is lost and nothing is hidden.** The state it was restating is
   * still stated once, above. The moment a report arrives the card comes back
   * with the five reasons it was built for. What goes is a card whose entire
   * content was a second copy of the card above it.
   */
  const nothingToBreakDown =
    sending.accepted === 0 ||
    (unreported && notDelivered.delayed === 0 && sending.complaints === 0);
  if (nothingToBreakDown) return null;

  return (
    <Card>
      <CardHeader className="p-[18px] pb-3.5">
        <CardTitle className="text-[0.95rem]">Not delivered</CardTitle>
        {/* Only the window here. It also used to explain "the five reasons" —
            over the nothing-sent and nothing-reported states, which list none —
            and sorted them wrongly: a hard bounce is already suppressed, and a
            soft bounce is not Amazon still working. Review found both; the
            sentence now sits over the reasons it describes. */}
        <p className="text-[0.82rem] leading-relaxed text-muted">
          Email only, {formatDate(window.since)} to {formatDate(window.until)}.
        </p>
      </CardHeader>

      <CardContent className="flex flex-col gap-6 p-[18px] pt-0">
        {/*
          THE `accepted === 0` ARM WAS HERE, AND IT WAS UNREACHABLE — SLICE 27.

          It printed *"No email was accepted in this window, so there is nothing
          to have arrived or not."* Slice 26 gave this card the
          `nothingToBreakDown` gate above, and the first thing that gate asks is
          `sending.accepted === 0` — so by the time this ternary is evaluated,
          accepted is never 0 and this arm could not render.

          Dead copy is worse than no copy: it reads as a live state to anybody
          auditing what this screen can say, and it is a sentence a reviewer has
          to think about every time. The state it described is still handled, one
          card up, by `SendingHealthTiles`.
        */}
        {unreported ? (
          <>
            <dl className="flex w-full flex-wrap gap-x-6 gap-y-4">
              <Figure
                label="Accepted"
                value={sending.accepted}
                caption="Amazon took these."
              />
              <Figure
                label="Awaiting events"
                value={sending.awaitingEvents}
                tone="quiet"
                caption="No final word from Amazon on any of them."
              />
              {/* A delay is a report too, of a kind that leaves a message
                  awaiting — so this state can hold on a workspace whose
                  notifications arrive perfectly. Shown where it happened, so
                  the sentence below is not read as "fix your setup" (review). */}
              {notDelivered.delayed > 0 && (
                <Figure
                  label="Delayed"
                  value={notDelivered.delayed}
                  tone="quiet"
                  caption="Amazon reported these as delayed, so its reports are reaching Wend."
                />
              )}
              {/* A complaint is a report of its own, so it is a count here while
                  delivered and bounced are not (review). */}
              {sending.complaints > 0 && (
                <Figure
                  label="Complaints"
                  value={sending.complaints}
                  caption="Recipients who marked this as spam. Amazon reported these."
                />
              )}
            </dl>
            <div className="flex flex-col gap-1.5 border-t border-line pt-5">
              <p className="text-[0.9rem] font-medium text-text">{explanation.title}</p>
              <UnreportedParagraphs explanation={explanation} size="text-[0.82rem]" />
            </div>
          </>
        ) : (
          <>
            <dl className="flex w-full flex-wrap gap-x-6 gap-y-4">
              <Figure
                label="Accepted"
                value={sending.accepted}
                caption="Amazon took these. The rates above are over the delivered and bounced among them."
              />
              {!onlyBounces && (
                <Figure
                  label="Delivered"
                  value={sending.delivered}
                  caption="A receiving server admitted them."
                />
              )}
              <Figure label="Bounced" value={sending.bounced} caption="Came back undelivered." />
              <Figure
                label="Complaints"
                value={sending.complaints}
                caption="Recipients who marked this as spam."
              />
              <Figure
                label="Awaiting events"
                value={sending.awaitingEvents}
                tone="quiet"
                caption="No final word from Amazon yet. Falls as reports arrive — except for email sent before delivery reports were set up."
              />
            </dl>
            {onlyBounces && (
              <p
                data-state="only-bounces-reported"
                className="max-w-prose text-[0.82rem] leading-relaxed text-muted"
              >
                No delivery is counted here because Amazon has reported none — only bounces — while
                others still wait. That usually means deliveries are not being reported to Wend,
                not that nothing arrived.
              </p>
            )}

            {onlyBounces ? (
              <p className="border-t border-line pt-5 text-[0.82rem] leading-relaxed text-muted">
                Why the bounced messages did not arrive — hard and soft are not summed, because one is
                a dead address and the other may still be delivered later.
              </p>
            ) : (
              <p className="border-t border-line pt-5 text-[0.82rem] leading-relaxed text-muted">
                Why messages did not arrive, or have not yet — a delayed or soft-bounced message can
                still be delivered later. The five are not summed into one failure figure, because
                each asks something different: fix the address, nothing, leave it on the list, wait,
                or — for a rendering failure — ours to fix.
              </p>
            )}
            <dl className="grid grid-cols-1 gap-x-6 gap-y-4 sm:grid-cols-2 lg:grid-cols-3">
              {/* With bounces and not one delivery reported, only the bounce rows
                  are counts anybody reported: a rejection or a delay reaches Wend
                  through the same reports that are missing the deliveries, so
                  "0 Rejected" and "0 Delayed" there are not facts (review). */}
              {NOT_DELIVERED_ROWS.filter(
                (row) => !onlyBounces || row.key === 'hardBounced' || row.key === 'softBounced',
              ).map((row) => (
                <div key={row.key} className="flex flex-col gap-1">
                  <dt className="flex items-baseline gap-2">
                    <span className="font-mono text-[1.1rem] tabular-nums text-text">
                      {formatCount(notDelivered[row.key])}
                    </span>
                    <span className="text-[0.85rem] font-medium text-text">{row.label}</span>
                  </dt>
                  <dd className="text-[0.76rem] leading-snug text-muted">{row.meaning}</dd>
                </div>
              ))}
            </dl>

            {bounces.undetermined > 0 && (
              <p className="max-w-prose text-[0.78rem] leading-relaxed text-muted">
                A further {pluralise(bounces.undetermined, 'bounce')} came back without Amazon saying
                whether it was permanent or temporary. They are not counted among the five, and not
                treated as retryable, because Amazon did not say they are.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
